import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { Alert, Button, Space, Typography } from "antd";
import { runInAction } from "mobx";

import { userStore } from "../stores/userStore";
import { SubscriptionManagePage } from "./SubscriptionManagePage";

import type { ProfileData } from "../api/profile";
import type {
  SubscriptionData,
  SubscriptionPlanRaw,
} from "../api/subscription";

const ALLOWED_HOSTS = ["localhost", "127.0.0.1", "my.nextgis.dev"];
const GB = 1024 ** 3;
const PROMO_CODE = "DEMO20";

const MOCK_PLANS: SubscriptionPlanRaw[] = [
  {
    id: "mandarin",
    name: "Mini",
    price_original: {
      monthly: { eur: 20, rub: 1200 },
      yearly: { eur: 180, rub: 10800 },
    },
    price_discounted: null,
    discount_annual: 25,
    discount_temporal: null,
  },
  {
    id: "pomelo",
    name: "Premium",
    price_original: {
      monthly: { eur: 100, rub: 6000 },
      yearly: { eur: 900, rub: 54000 },
    },
    price_discounted: null,
    discount_annual: 25,
    discount_temporal: null,
  },
];

type QuoteMode = "normal" | "slow" | "error";

interface Scenario {
  id: string;
  label: string;
  description: string;
  initialPlan: "mandarin" | "pomelo";
  initialPeriod: "monthly" | "yearly";
  country: "DE" | "RU";
  subscription: SubscriptionData;
  quoteMode?: QuoteMode;
  legalOrdersDisabled?: boolean;
}

const makeSubscription = (
  plan: "lemon" | "mandarin" | "pomelo",
  interval: "monthly" | "yearly" | null,
  autopayment: boolean,
  userLimit: number,
  storageGb: number,
): SubscriptionData => ({
  autopayment,
  plan,
  interval,
  price: "0.00",
  currency: "EUR",
  expire_at: new Date(Date.now() + 25 * 24 * 60 * 60 * 1000).toISOString(),
  options: null,
  canceled_at: null,
  trial_active: false,
  allow_trial: plan === "lemon",
  payment_method: autopayment
    ? {
        type: "card",
        status: "success",
        card: {
          brand: "visa",
          last4: "4242",
          exp_month: 12,
          exp_year: 2028,
        },
      }
    : null,
  user_settings: {
    user_limit: userLimit,
    storage_limit: storageGb * GB,
    resource_limit: null,
  },
});

const SCENARIOS: Scenario[] = [
  {
    id: "new-premium",
    label: "New Premium",
    description: "International card checkout without an active subscription",
    initialPlan: "pomelo",
    initialPeriod: "monthly",
    country: "DE",
    subscription: makeSubscription("lemon", null, false, 1, 5),
  },
  {
    id: "new-mini",
    label: "New Mini yearly",
    description: "Mini fixed limits and annual discount",
    initialPlan: "mandarin",
    initialPeriod: "yearly",
    country: "DE",
    subscription: makeSubscription("lemon", null, false, 1, 5),
  },
  {
    id: "ru-wire",
    label: "RU card / wire",
    description: "Eligible legal-entity switch and invoice form",
    initialPlan: "pomelo",
    initialPeriod: "monthly",
    country: "RU",
    subscription: makeSubscription("lemon", null, false, 1, 5),
  },
  {
    id: "ru-wire-disabled",
    label: "RU wire disabled",
    description: "Russian checkout when legal-entity orders are disabled",
    initialPlan: "pomelo",
    initialPeriod: "monthly",
    country: "RU",
    legalOrdersDisabled: true,
    subscription: makeSubscription("lemon", null, false, 1, 5),
  },
  {
    id: "active-mini",
    label: "Active Mini",
    description: "Existing autopayment with fixed Mini limits",
    initialPlan: "mandarin",
    initialPeriod: "monthly",
    country: "DE",
    subscription: makeSubscription("mandarin", "monthly", true, 1, 5),
  },
  {
    id: "active-premium",
    label: "Active Premium",
    description: "Custom limits and protected lower bounds",
    initialPlan: "pomelo",
    initialPeriod: "monthly",
    country: "DE",
    subscription: makeSubscription("pomelo", "monthly", true, 15, 100),
  },
  {
    id: "renew-premium",
    label: "Premium renewal",
    description: "Paid plan without autopayment",
    initialPlan: "pomelo",
    initialPeriod: "yearly",
    country: "DE",
    subscription: makeSubscription("pomelo", "yearly", false, 10, 50),
  },
  {
    id: "slow-quote",
    label: "Slow quote",
    description: "Displays quote and total loading states for 2.5 seconds",
    initialPlan: "pomelo",
    initialPeriod: "monthly",
    country: "DE",
    quoteMode: "slow",
    subscription: makeSubscription("lemon", null, false, 1, 5),
  },
  {
    id: "quote-error",
    label: "Quote error",
    description: "Pricing failure and unavailable checkout state",
    initialPlan: "pomelo",
    initialPeriod: "monthly",
    country: "DE",
    quoteMode: "error",
    subscription: makeSubscription("lemon", null, false, 1, 5),
  },
];

const DEFAULT_SCENARIO = SCENARIOS[0];

const mockProfile = (country: "DE" | "RU"): ProfileData => ({
  username: "manage-preview",
  first_name: country === "RU" ? "Иван" : "Test",
  last_name: country === "RU" ? "Иванов" : "User",
  email: "manage-preview@example.com",
  phone: country === "RU" ? "+7 900 000-00-00" : "+49 30 000000",
  locale: country === "RU" ? "ru" : "en",
  company: country === "RU" ? "ООО «Тест»" : "Test Company GmbH",
  industry: "it",
  role: "employee",
  country,
  email_confirmed: true,
  integrations: { social_list: [] },
});

const jsonResponse = (data: unknown, status = 200) =>
  new Response(JSON.stringify(data), {
    status,
    headers: { "Content-Type": "application/json" },
  });

const waitFor = (milliseconds: number, signal?: AbortSignal | null) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = window.setTimeout(resolve, milliseconds);
    signal?.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });

const createMockFetch =
  (scenarioRef: React.RefObject<Scenario>): typeof window.fetch =>
  async (input, init) => {
    const requestUrl =
      input instanceof Request
        ? input.url
        : input instanceof URL
          ? input.href
          : input;
    const url = new URL(requestUrl, window.location.origin);
    const scenario = scenarioRef.current;

    if (url.pathname === "/api/v1/subscription/prices/") {
      return jsonResponse(MOCK_PLANS);
    }

    if (url.pathname === "/api/v1/subscription/price/") {
      if (scenario.quoteMode === "slow") await waitFor(2500, init?.signal);
      if (scenario.quoteMode === "error") {
        return jsonResponse({ error: "Mock quote failure" }, 500);
      }

      const body = JSON.parse(String(init?.body ?? "{}")) as {
        plan?: string;
        interval?: "monthly" | "yearly";
        coupon?: string;
        settings?: { user_limit?: number; storage_limit?: number };
      };
      const planId = body.plan ?? url.searchParams.get("plan") ?? "pomelo";
      const users = body.settings?.user_limit ?? 5;
      const storageGb = (body.settings?.storage_limit ?? 50 * GB) / GB;
      const monthlyEur =
        planId === "mandarin"
          ? 20
          : Math.round(30 + users * 4 + storageGb * 0.2);
      const monthlyRub = monthlyEur * 60;
      const original = {
        monthly: { eur: monthlyEur, rub: monthlyRub },
        yearly: { eur: monthlyEur * 9, rub: monthlyRub * 9 },
      };
      const couponValid = body.coupon === PROMO_CODE;
      const discounted = couponValid
        ? {
            monthly: {
              eur: original.monthly.eur * 0.8,
              rub: original.monthly.rub * 0.8,
            },
            yearly: {
              eur: original.yearly.eur * 0.8,
              rub: original.yearly.rub * 0.8,
            },
          }
        : original;

      return jsonResponse({
        id: planId,
        name: planId === "mandarin" ? "Mini" : "Premium",
        price_original: original,
        price_discounted: discounted,
        discount_annual: 25,
        discount_temporal: couponValid ? 20 : null,
        coupon_status: body.coupon
          ? couponValid
            ? "ok"
            : "err_not_found"
          : "no_coupon",
        coupon_permanent: false,
      });
    }

    if (url.pathname === "/api/subscription/convertion/") {
      if (scenario.quoteMode === "slow") await waitFor(2500, init?.signal);
      if (scenario.quoteMode === "error") {
        return jsonResponse({ error: "Mock conversion failure" }, 500);
      }
      return jsonResponse({
        subscription: {
          expire_at: new Date(
            Date.now() + 30 * 24 * 60 * 60 * 1000,
          ).toISOString(),
        },
        convertion: {
          additional_days: scenario.subscription.plan === "lemon" ? 0 : 15,
        },
      });
    }

    if (url.pathname === "/api/autopayment/") {
      return jsonResponse({ redirect_url: window.location.pathname });
    }

    if (url.pathname === "/api/v1/subscription/invoice/") {
      return jsonResponse({ invoice_status: "created" });
    }

    return jsonResponse(
      { error: `No mock configured for ${url.pathname}` },
      501,
    );
  };

export const TestSubscriptionManagePage = () => {
  const { i18n } = useTranslation();
  const [scenario, setScenario] = useState(DEFAULT_SCENARIO);
  const [ready, setReady] = useState(false);
  const [completedUrl, setCompletedUrl] = useState<string | null>(null);
  const scenarioRef = useRef(DEFAULT_SCENARIO);
  const i18nRef = useRef(i18n);
  const savedState = useRef({
    fetch: window.fetch,
    profile: userStore.profile,
    subscription: userStore.subscription,
    language: i18n.language,
    legalOrdersDisabled: (window as unknown as Record<string, unknown>)
      .isLegalEntityOrderDisabled,
  });

  const applyScenario = useCallback((nextScenario: Scenario) => {
    scenarioRef.current = nextScenario;
    (window as unknown as Record<string, unknown>).isLegalEntityOrderDisabled =
      Boolean(nextScenario.legalOrdersDisabled);
    userStore.setProfile(mockProfile(nextScenario.country));
    userStore.setSubscription({
      ...nextScenario.subscription,
      currency: nextScenario.country === "RU" ? "RUB" : "EUR",
    });
    void i18nRef.current.changeLanguage(
      nextScenario.country === "RU" ? "ru" : "en",
    );
  }, []);

  useEffect(() => {
    const originalState = savedState.current;
    const i18nInstance = i18nRef.current;
    window.fetch = createMockFetch(scenarioRef);
    applyScenario(DEFAULT_SCENARIO);
    const readyTimer = window.setTimeout(() => setReady(true), 0);

    return () => {
      window.clearTimeout(readyTimer);
      window.fetch = originalState.fetch;
      (
        window as unknown as Record<string, unknown>
      ).isLegalEntityOrderDisabled = originalState.legalOrdersDisabled;
      runInAction(() => {
        userStore.profile = originalState.profile;
        userStore.subscription = originalState.subscription;
      });
      void i18nInstance.changeLanguage(originalState.language);
    };
  }, [applyScenario]);

  if (!ALLOWED_HOSTS.includes(window.location.hostname)) {
    return <Navigate to="/" replace />;
  }

  const selectScenario = (nextScenario: Scenario) => {
    applyScenario(nextScenario);
    setCompletedUrl(null);
    setScenario(nextScenario);
  };

  return (
    <div style={{ padding: "24px 32px 48px" }}>
      <Typography.Title level={4} style={{ marginBottom: 4 }}>
        Subscription Manage UI
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        Fully mocked. Try promo code <code>{PROMO_CODE}</code>; payment actions
        are intercepted locally.
      </Typography.Paragraph>

      <Space wrap size={[8, 8]} style={{ marginBottom: 12 }}>
        {SCENARIOS.map((item) => (
          <Button
            key={item.id}
            type={scenario.id === item.id ? "primary" : "default"}
            onClick={() => selectScenario(item)}
          >
            {item.label}
          </Button>
        ))}
      </Space>

      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        {scenario.description}
      </Typography.Paragraph>

      {completedUrl && (
        <Alert
          type="success"
          showIcon
          closable
          title="Mock payment completed"
          description={`Redirect intercepted: ${completedUrl}`}
          onClose={() => setCompletedUrl(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {ready && (
        <SubscriptionManagePage
          key={scenario.id}
          initialPlan={scenario.initialPlan}
          initialPeriod={scenario.initialPeriod}
          onPaymentComplete={setCompletedUrl}
        />
      )}
    </div>
  );
};
